import { Injectable } from '@angular/core';
import { FortigatesService } from '@app/features/fortigates/shared/fortigates.service';
import { LocationsService } from '@app/features/locations/shared/locations.service';
import { Location } from '@app/shared/models/location.model';
import { Fortigate } from '@app/shared/models/fortigate/fortigate.model';
import { FortigateLocation } from '@app/shared/models/fortigate/fortigate-location.model';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ChainWideFortigatesService {
    constructor(
        private fortigatesService: FortigatesService,
        private locationsService: LocationsService
    ) { }

    async getAllFortigates(): Promise<Fortigate[]> {
        const allFortigates: Fortigate[] = [];
        try {
            const observable = await this.fortigatesService.getAllFortigates();
            const fortigates_from_response = await firstValueFrom(observable);
            if (typeof fortigates_from_response === 'string') {
                console.log("Fortigates timed out!")
                return allFortigates;
            }
            fortigates_from_response["message"].forEach(fortigate_json => {
                let fortigate: Fortigate = fortigate_json;
                allFortigates.push(fortigate);
            });
        } catch (error) {
            console.log("Fortigates timed out!")
        }
        return allFortigates;
    }

    async getAllLocations(): Promise<Location[]> {
        const locations: Location[] = [];
        try {
            const observable = await this.locationsService.getAllLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return locations;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                let location: Location = new Location;
                location.name = locations_json["core"]["locationName"];
                location.number = locations_json["locationNumber"];
                location.status = locations_json["locationStatus"]["locationStatus"];

                if (!locations_json["core"].hasOwnProperty('timeZone')
                    || !locations_json["core"]["timeZone"].hasOwnProperty('timeZoneName')) {
                    location.timezone = "";
                } else {
                    location.timezone = locations_json["core"]["timeZone"]["timeZoneName"].split(" ")[2];
                }

                // Some locations don't have operators, such as locations with a 'future' state
                if (!locations_json.hasOwnProperty('operator')) {
                    location.operatorName = "";
                } else {
                    location.operatorName = locations_json["operator"]["name"];
                }
                locations.push(location);
            });
        } catch (error) {
            console.log(error);
        }
        return locations;
    }

    private sortFortigates(allFortigates: Fortigate[]): Map<string, Fortigate[]> {
        let sortedFortigates = new Map<string, Fortigate[]>();
        for (let fortigate of allFortigates) {
            let storeNumber = "";

            if (fortigate.hostname != null) {
                storeNumber = fortigate.hostname.substring(0, 5);
            }

            if (sortedFortigates.has(storeNumber)) {
                sortedFortigates.get(storeNumber).push(fortigate);
            } else {
                let fortigates = [];
                fortigates.push(fortigate);
                sortedFortigates.set(storeNumber, fortigates);
            }
        }
        return sortedFortigates;
    }

    private sortLocations(locations: Location[]): Map<string, Location> {
        let sortedLocations = new Map<string, Location>();
        locations.forEach(location => {
            sortedLocations.set(location.number, location);
        });
        return sortedLocations;
    }

    async createDataSource(locations?: Location[]): Promise<FortigateLocation[]> {
        const fortigateLocations: FortigateLocation[] = [];
        let locationsToUse: Location[];

        if (locations == null) {
            locationsToUse = await this.getAllLocations();
        } else {
            locationsToUse = locations;
        }

        const allFortigates = await this.getAllFortigates();
        const sortedFortigates = this.sortFortigates(allFortigates);
        const sortedLocation = this.sortLocations(locationsToUse);

        sortedFortigates.forEach((value: Fortigate[], key: string) => {
            if (sortedLocation.has(key)) {
                let fortigateLocation: FortigateLocation = new FortigateLocation;
                fortigateLocation.fortigates = value;
                fortigateLocation.location = sortedLocation.get(key);
                fortigateLocations.push(fortigateLocation);
            }
        });

        return fortigateLocations;
    }
}
